import { useAuth0 } from "@auth0/auth0-react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Dropdown, Modal, Table } from "antd";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import AddCardModal from "../../modals/AddCardModal";
import SuccessModal from "../../modals/SuccessModal/index.jsx";
import UpdateCardModal from "../../modals/UpdateCardModal";
import AddButton from "../../shared/AddButton";
import BillingWelcomeNotice from "../../components/BillingWelcomeNotice";
import {
  deletePaymentMethod,
  getDefaultMethod,
  getPaymentMethods,
  markAsDefault,
} from "../../utils/payment_methods";

import { observer } from "mobx-react-lite";
import { useDispatch, useSelector } from "react-redux";
import { setAddCardModalOpen } from "../../store/common";

const PaymentMethods = observer(() => {
  // const hostname = window.location.hostname;
  // This should be maintain from ENV
  // let stripe_api_key = "pk_test_51ON56DHZ3r5Se0To3yARf9k2ljtD8YvPjHZlbxsFmJI9eovhM9bWwlLh3iod55stJEFEGMx13DxVPNUCd6M10QHl00B3a08Se8";
  // if (hostname == "au.wowzerbackupandrestore.com") {
  //   stripe_api_key = "pk_live_51ON56DHZ3r5Se0To8K4z940h4DEpHmbo24b4cAmguAT474pQJI5APggWWghpvPpxOUMLMghBnyGIJgy2ETbDv21300nqUo99QK";
  // }

  const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`);
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [editSelected, setEditSelected] = useState();
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const dispatch = useDispatch();

  const [successTitle, setSuccessTitle] = useState("");
  const [successText, setSuccessText] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const [cards, setCards] = useState([]);
  const [updateCardModalOpen, setUpdateCardModalOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (!isLoading) {
      fetchPaymentMethods();
      fetchDefaultMethod();
    }
  }, [isLoading]);

  const fetchPaymentMethods = async () => {
    try {
      setPaymentMethodsLoading(true);

      const token = await getIdTokenClaims();

      getPaymentMethods(token.__raw)
        .then((res) => {
          if (res.data.success === true) {
            setCards(res.data.cards.data);
          } else {
            console.log(res.data);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setPaymentMethodsLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDefaultMethod = async () => {
    try {
      const token = await getIdTokenClaims();

      getDefaultMethod(token.__raw)
        .then((res) => {
          if (res.data.success) {
            setDefaultMethod(res.data.default_method);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const onNewPaymentMethodAdd = (paymentMethodId, message) => {
    const text =
      typeof message === "string"
        ? message
        : typeof paymentMethodId === "string" && !paymentMethodId.startsWith("pm_")
        ? paymentMethodId
        : "Payment method added successfully";
    setSuccessTitle("Payment method added");
    setSuccessText(text);
    setSuccessModalOpen(true);

    fetchPaymentMethods();
  };

  const makePaymentMethodDefault = async (id) => {
    try {
      const token = await getIdTokenClaims();

      markAsDefault(token.__raw, id)
        .then(() => {
          setDefaultMethod(id);
          fetchPaymentMethods();
        })
        .catch((err) => {
          enqueueSnackbar(
            "Something went wrong while marking the payment method as default",
            {
              variant: "error",
            }
          );
          console.error(err);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleCardUpdate = () => {
    fetchPaymentMethods();
    setUpdateCardModalOpen(false);
  };

  const handleCardDelete = async (id) => {
    try {
      const token = await getIdTokenClaims();

      setPaymentMethodsLoading(true);

      deletePaymentMethod(token.__raw, id)
        .then((res) => {
          if (res.data.success) {
            enqueueSnackbar(res.data.message, {
              variant: "success",
            });
            fetchPaymentMethods();
          } else {
            enqueueSnackbar(res.data.message, {
              variant: "error",
            });
            console.error(res.data);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setPaymentMethodsLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const columns = [
    {
      title: "Name on card",
      dataIndex: "name",
      key: "name",
      render: (_, { billing_details }) => (
        <div className="flex gap-2 items-center text-gray-800 break-words">
          {billing_details.name}
        </div>
      ),
      responsive: ["md"],
    },
    {
      title: "Card number",
      dataIndex: "number",
      key: "number",
      render: (_, { card }) => (
        <div className="flex gap-3 items-center text-gray-800 flex-wrap">
          <span>**** **** {card.last4}</span>
          <img
            className="h-5 w-9"
            src={"/assets/image/" + card.display_brand + ".png"}
            alt={card.display_branch}
          />
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
      render: (_, { billing_details }) => (
        <div className="flex gap-2 items-center text-gray-800 break-words">
          {billing_details.email}
        </div>
      ),
      responsive: ["lg"],
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry",
      key: "expiry",
      render: (_, { card }) => (
        <div className="flex gap-2 items-center text-gray-800">
          {card.exp_month}/{card.exp_year}
        </div>
      ),
      responsive: ["sm"],
    },
    {
      title: "Entry Date",
      dataIndex: "entry",
      key: "entry",
      render: (_, { created_at }) => (
        <div className="flex gap-2 items-center text-gray-800">
          {dayjs(created_at).format("DD-MM-YYYY")}
        </div>
      ),
      responsive: ["sm"],
    },
    {
      title: "Action",
      dataIndex: "default",
      key: "default",
      align: "right",
      render: (_, payment_method) => {
        return (
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {payment_method.id === defaultMethod ? (
              <div className="text-blue-400 flex items-center gap-2 whitespace-nowrap mr-2">
                <ReactSVG src={"/assets/icons/green-check.svg"} />
                Default
              </div>
            ) : (
              <div
                className="px-2 cursor-pointer font-medium py-1 mr-2 rounded-lg border border-gray-300 text-sm whitespace-nowrap"
                onClick={() => {
                  setPaymentMethodsLoading(true);
                  makePaymentMethodDefault(payment_method.id);
                }}
              >
                Mark Default
              </div>
            )}
            <Dropdown
              placement="bottomRight"
              trigger={["click"]}
              dropdownRender={() => (
                <div className="bg-white w-36 p-2 border border-gray-100 rounded-lg shadow-lg">
                  <div
                    className="font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    onClick={() => {
                      setEditSelected(payment_method);
                      setUpdateCardModalOpen(true);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/modify.svg"} />
                    Edit
                  </div>
                  <div
                    className="font-medium flex items-center gap-2 text-rose-500 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    onClick={() => handleCardDelete(payment_method.id)}
                  >
                    <ReactSVG src={"/assets/icons/delete.svg"} />
                    Delete
                  </div>
                </div>
              )}
            >
              <div className="inline-block">
                <ReactSVG
                  className="p-1 cursor-pointer rounded-lg hover:bg-white"
                  src={"/assets/icons/dot-menu.svg"}
                />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const renderMobileCards = () => {
    if (!cards || cards.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No payment methods found
        </div>
      );
    }

    return cards.map((item, index) => (
      <div
        key={index}
        className="border border-gray-200 rounded-lg p-4 mb-4 shadow-sm"
      >
        <div className="flex justify-between items-start mb-3">
          <div className="font-medium text-gray-800 break-words pr-2">
            {item.billing_details.name}
          </div>
          <div className="flex items-center gap-2">
            {item.id === defaultMethod && (
              <div className="text-blue-400 flex items-center gap-1 text-sm">
                <ReactSVG src={"/assets/icons/green-check.svg"} />
                <span className="whitespace-nowrap">Default</span>
              </div>
            )}
            <Dropdown
              placement="bottomRight"
              trigger={["click"]}
              dropdownRender={() => (
                <div className="bg-white w-36 p-2 border border-gray-100 rounded-lg shadow-lg">
                  {item.id !== defaultMethod && (
                    <div
                      className="font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                      onClick={() => {
                        setPaymentMethodsLoading(true);
                        makePaymentMethodDefault(item.id);
                      }}
                    >
                      <ReactSVG src={"/assets/icons/green-check.svg"} />
                      Make Default
                    </div>
                  )}
                  <div
                    className="font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    onClick={() => {
                      setEditSelected(item);
                      setUpdateCardModalOpen(true);
                    }}
                  >
                    <ReactSVG src={"/assets/icons/modify.svg"} />
                    Edit
                  </div>
                  <div
                    className="font-medium flex items-center gap-2 text-rose-500 rounded-lg hover:bg-gray-50 p-2 text-sm cursor-pointer"
                    onClick={() => handleCardDelete(item.id)}
                  >
                    <ReactSVG src={"/assets/icons/delete.svg"} />
                    Delete
                  </div>
                </div>
              )}
            >
              <ReactSVG
                className="p-1 cursor-pointer rounded-lg hover:bg-gray-50"
                src={"/assets/icons/dot-menu.svg"}
              />
            </Dropdown>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-gray-600">**** **** {item.card.last4}</span>
          <img
            className="h-5 w-9"
            src={`/assets/image/${item.card.display_brand}.png`}
            alt={item.card.display_brand}
          />
        </div>

        <div className="grid grid-cols-2 gap-y-2 text-sm">
          {item.billing_details.email && (
            <div className="col-span-2">
              <span className="text-gray-500">Email: </span>
              <span className="text-gray-800 break-words">
                {item.billing_details.email}
              </span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Expiry: </span>
            <span className="text-gray-800">
              {item.card.exp_month}/{item.card.exp_year}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Entry: </span>
            <span className="text-gray-800">
              {dayjs(item.created_at).format("DD-MM-YYYY")}
            </span>
          </div>
        </div>
      </div>
    ));
  };

  const addCardModalOpen = useSelector(
    (state) => state.commonReducer.addCardModalOpen
  );

  return (
    <div className="max-w-full overflow-hidden my-2">
      <Elements stripe={stripePromise}>
        <AddCardModal
          modalOpen={addCardModalOpen}
          onCreate={onNewPaymentMethodAdd}
          makeDefaultPaymentMethod={makePaymentMethodDefault}
        />
      </Elements>

      <Modal
        title={
          <div className="text-xl md:text-2xl">Updated Card Information</div>
        }
        centered
        open={updateCardModalOpen}
        footer={null}
        onOk={() => setUpdateCardModalOpen(false)}
        onCancel={() => setUpdateCardModalOpen(false)}
        destroyOnClose={true}
        zIndex={300}
        width={windowWidth > 768 ? 520 : "95%"}
        className="responsive-modal"
      >
        <UpdateCardModal
          paymentMethod={editSelected}
          onUpdate={handleCardUpdate}
        />
      </Modal>

      {!paymentMethodsLoading && cards.length === 0 && (
        <BillingWelcomeNotice variant="compact" className="mb-4" />
      )}

      <div className="flex items-center justify-end mb-4">
        {/* <h2 className="text-lg font-medium md:text-xl">Payment Methods</h2> */}
        <AddButton
          text={windowWidth < 640 ? "Add" : "Add New"}
          clickMethod={() => dispatch(setAddCardModalOpen(true))}
        />
      </div>

      {paymentMethodsLoading ? (
        <div className="flex justify-center py-8">
          <div className="loading-spinner">Loading...</div>
        </div>
      ) : (
        <>
          <div className="mb-10 pb-5 hidden md:block">
            <Table
              dataSource={cards}
              columns={columns}
              loading={paymentMethodsLoading}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                defaultPageSize: 10,
                responsive: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              scroll={{ x: "max-content" }}
              rowKey={(record) => record.id}
              size={windowWidth < 1024 ? "small" : "middle"}
            />
          </div>
          <div className="md:hidden">{renderMobileCards()}</div>
        </>
      )}

      <SuccessModal
        title={successTitle}
        text={successText}
        modalOpen={successModalOpen}
        onModalClose={() => setSuccessModalOpen(false)}
      />
    </div>
  );
});

export default PaymentMethods;
